import React, { useEffect, useRef, useState } from 'react';
import NotesSection from '../CalenderNotesSection';
import CalenderLeftIcon from '../../../assets/icon/CalenderLeftIcon';
import CalenderRightIcon from '../../../assets/icon/CalenderRightIcon';
import CalenderUpIcon from '../../../assets/icon/CalenderUpIcon';
import useBgColorStore from '../../../store/bgColor';
import useLocationStore from '../../../store/locatonStore';
import useTimeStore from '../../../store/timeStore';
import CalenderScreen from '../CalenderScreen';
import CalenderDownIcon from '../../../assets/icon/CalenderDownIcon';
import CalenderUpIconDark from '../../../assets/icon/CalenderUpIconDark';
import CalenderDownIconDark from '../../../assets/icon/CalenderDownIconDark';
import CalenderLeftIconDark from '../../../assets/icon/CalenderLeftIconDark';
import CalenderRightIconDark from '../../../assets/icon/CalenderRightIconDark';
import { CUSTOM_TOAST_ID } from '../../../constants/constant';
import { showOrReplaceToast } from '../../../utils/utility';
import Loader from '../../../assets/loader/Loader';
import { useCalendarData } from '../../../hooks/apiHook/operationDetails';
import useDateStore from '../../../store/selectedDateStore';

interface CalendarDayData {
  date: string;
  bookingRate: number;
  availability: string;
  booked: number;
  comment: string;
  isStaffMissing: boolean;
  staffMissing: string;
  mediaTravelingMessage: string;
  vipBookingMessage: string;
}

interface CalendarHeaderProps {
  refreshCalender?: number;
}

const CalendarHeader: React.FC<CalendarHeaderProps> = ({ refreshCalender }) => {
  const [calendarData, setCalendarData] = useState<CalendarDayData[]>([]);
  const { bgColor } = useBgColorStore();
  const selectedLocation = useLocationStore((state) => state.selectedLocation);

  const operationTime = useTimeStore();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [ShowCalenderComponent, setShowCalenderComponent] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const ownerId = selectedLocation.OwnerId
  useEffect(() => {
    refetchCalendarData();
  }, [refreshCalender]);

  const {
    data: calendarApiData,
    isLoading,
    isFetching,
    refetch: refetchCalendarData,
    error,
  } = useCalendarData(
    operationTime?.rateId ?? null,
    ownerId ?? null,
  );

  useEffect(() => {
    if (calendarApiData) {
      setCalendarData(calendarApiData);
    }
  }, [calendarApiData]);

  useEffect(() => {
    if (error) {
      showOrReplaceToast(
        CUSTOM_TOAST_ID.calender,
        'Failed to fetch calendar data',
        'error',
      );
    }
  }, [error]);

  const checkScrollability = () => {
    const container = scrollRef.current;
    if (container) {
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      setIsScrollable(hasHorizontalScroll);
    }
  };

  useEffect(() => {
    checkScrollability();
    window.addEventListener('resize', checkScrollability);
    return () => {
      window.removeEventListener('resize', checkScrollability);
    };
  }, []);

  const formatDateKey = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const getDayName = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -150, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 150, behavior: 'smooth' });
    }
  };

  const toggleExtraComponent = () => {
    setShowCalenderComponent((prev) => !prev);
  };

  const { setSelectedDateFromStore } = useDateStore();
  const handleSelectedDate = (dateStr: string) => {
    const dateObj = new Date(dateStr);
    setSelectedDateFromStore(dateObj);
  };

  return (
    <div className="flex flex-col w-screen lg:w-full p-2">
      <div className="flex items-center">
        {isScrollable && (
          <button className="hidden sm:block p-2" onClick={scrollLeft}>
            <div>
              <div className="block dark:hidden">
                <CalenderLeftIcon />
              </div>
              <div className="hidden dark:block">
                <CalenderLeftIconDark />
              </div>
            </div>
          </button>
        )}

        {/* Desktop View */}
        <div
          ref={scrollRef}
          className=" relative hidden sm:flex overflow-x-auto justify-between w-full"
        >
          {isLoading || isFetching ? (
            <div className="flex ">
              <Loader />
            </div>
          ) : (
            <>
              {calendarData?.map((day, index) => (
                <div
                  onClick={() => {
                    handleSelectedDate(day?.date);
                  }}
                  key={day?.date}
                  className={`${bgColor} relative group flex flex-col w-full items-center rounded-md mx-1 p-2 cursor-pointer`}
                >
                  {(day?.staffMissing ||
                    day?.mediaTravelingMessage ||
                    day?.vipBookingMessage) && (
                      <div
                        className={`absolute p-2 hidden group-hover:flex z-50 whitespace-nowrap bg-white text-gray text-xs font-normal rounded text-center 
    ${index < 7 ? 'left-0 ml-2' : 'right-0 mr-2'}`}
                      >
                        <div className="flex flex-col text-left">
                          {day?.staffMissing && <span>{day.staffMissing}</span>}
                          {day?.mediaTravelingMessage && (
                            <span>{day.mediaTravelingMessage}</span>
                          )}
                          {day?.vipBookingMessage &&
                            day?.vipBookingMessage?.split('&#013;')?.map((line, index) => (
                              <span key={index}>
                                {line}
                                <br />
                              </span>
                            ))}

                        </div>
                      </div>
                    )}

                  <div
                    className={`${day?.isStaffMissing ? 'text-red-500' : 'text-blue'} font-medium text-sm`}
                  >
                    {getDayName(day?.date)}
                    <span className="text-blue">
                      {day?.mediaTravelingMessage && '*'}
                    </span>
                    <span className="text-red-600">
                      {day?.vipBookingMessage && '*'}
                    </span>
                  </div>

                  <div className="text-sm text-text-primary">
                    {formatDateKey(day?.date)}
                  </div>
                  <div className="text-sm">
                    <span className="text-textColorGray">{day?.booked}</span>
                    <span className="text-textColorGray">/</span>
                    <span className="text-green">{day?.availability}</span>
                  </div>
                  <div
                    className={`text-xs ${day?.bookingRate > 0
                        ? 'text-green'
                        : day?.bookingRate < 0
                          ? 'text-red-500'
                          : 'text-text-customGrey'
                      }`}
                  >
                    {isLoading || isFetching ? (
                      <Loader />
                    ) : (
                      <>
                        {day?.bookingRate > 0 && '+'}
                        {day?.bookingRate < 0 && '-'}
                        {Math.abs(day?.bookingRate || 0)}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Mobile View */}
        <div className="flex sm:hidden overflow-x-auto w-full justify-between no-scrollbar">
          {calendarData?.slice(0, 4).map((day) => (
            <div
              onClick={() => {
                handleSelectedDate(day?.date);
              }}
              key={day?.date}
              className={`${bgColor} flex flex-col items-center rounded-md p-2 text-center mx-1 w-full cursor-pointer `}
            >
              <div
                className={`${day?.isStaffMissing ? 'text-red-500' : 'text-blue'} font-medium text-sm`}
              >
                {getDayName(day?.date)}
              </div>
              <div className="text-sm text-text-primary">
                {formatDateKey(day?.date)}
              </div>
              <div className="text-sm">
                <span className="text-textColorGray">{day?.booked}</span>
                <span className="text-textColorGray">/</span>
                <span className="text-calenderColor-topHeader">
                  {day?.availability}
                </span>
              </div>
              <div
                className={`text-xs ${day?.bookingRate > 0
                    ? 'text-green'
                    : day?.bookingRate < 0
                      ? 'text-red-500'
                      : 'text-text-customGrey'
                  }`}
              >
                {isLoading || isFetching ? (
                  <Loader />
                ) : (
                  <>
                    {day?.bookingRate > 0 && '+'}
                    {day?.bookingRate < 0 && '-'}
                    {Math.abs(day?.bookingRate || 0)}
                  </>
                )}
              </div>
            </div>
          ))}
          <div className="flex rounded-sm border-[.5px] border-[#DFE4EA] max-w-[84px]">
            <button
              className="block sm:hidden p-2"
              onClick={toggleExtraComponent}
            >
              {ShowCalenderComponent ? (
                <div>
                  <div className="block dark:hidden">
                    <CalenderUpIcon />
                  </div>
                  <div className="hidden dark:block">
                    <CalenderUpIconDark />
                  </div>
                </div>
              ) : (
                <div>
                  <div className="block dark:hidden">
                    <CalenderDownIcon />
                  </div>
                  <div className="hidden dark:block">
                    <CalenderDownIconDark />
                  </div>
                </div>
              )}
            </button>
          </div>
        </div>

        {isScrollable && (
          <button className="hidden sm:block p-2" onClick={scrollRight}>
            <div>
              <div className="block dark:hidden">
                <CalenderRightIcon />
              </div>
              <div className="hidden dark:block">
                <CalenderRightIconDark />
              </div>
            </div>
          </button>
        )}
      </div>

      {ShowCalenderComponent && (
        <div className="mt-2">
          <CalenderScreen />
        </div>
      )}

      <div className="mt-4 justify-center px-1 sm:px-12">
        <NotesSection ownerId={selectedLocation?.OwnerId} />
      </div>
    </div>
  );
};

export default CalendarHeader;
